import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'

import * as gs from '../../../app/gStyles'

import ContractLogo from './ContractLogo'
import ReportPages, { horizontalPad } from './ReportPages'
//@ts-ignore
import styles from './SecurityContract.module.css'

export type CertOfCompletionProps = c.r<{
  customerID: string
  isPDF: boolean
  /**
   * CE (Caballero's Electric) contract, per Maria G/Jaron — same "cameras,
   * doorbell, home automation, homebase, never alarm" constraint as the CE
   * contract's own Scope of Work (T56). Swaps the "System Installed" list's
   * source from `useDetailLines` (unrestricted, e.g. `security_system`'s
   * `type` values like "Alarm panel") to the alarm-safe
   * `useSecurityScopeDetailLines`.
   */
  alarmSafe?: boolean
  noPDF?: boolean
  readOnly?: boolean
  toggleSecCompletionSignature(): void
}>

export default React.memo<CertOfCompletionProps>(function CertOfCompletion({
  alarmSafe,
  customerID,
  isPDF,
  noPDF,
  readOnly,
  toggleSecCompletionSignature,
}) {
  //#region styling
  //#endregion
  //#region global
  const { contractCompanyFull } = r.useContractCompany(customerID)

  const [customerData] = r.useCustomer(customerID)

  const {
    customerAddress,
    customerEmail,
    customerName,
    customerPhone,
    homeRep,

    security_completion_sign_customer,
    security_completion_sign_customer_date,

    security_contract_sign_company,
    security_contract_sign_customer,
    solarCompany,
  } = customerData

  const [coBuyer, , coBuyerHandle] = r.useCustomerFieldInput(
    customerID,
    'security_contract_co_buyer',
  )
  const [workOrderVal, , workOrderHandle] = r.useCustomerFieldInput(
    customerID,
    'security_completion_work_order',
  )
  const [completionDateVal, , completionDateHandle] = r.useCustomerFieldInput(
    customerID,
    'security_completion_date',
  )
  //#endregion
  //#region local

  // Both hooks called unconditionally (rules-of-hooks) — pick which result
  // to render below via `alarmSafe`.
  const { detailLines: allDetailLines } = r.useDetailLines(customerID)
  const { detailLines: securityScopeDetailLines } =
    r.useSecurityScopeDetailLines(customerID)
  const detailLines = alarmSafe ? securityScopeDetailLines : allDetailLines

  //#endregion

  const buttonSignatureStyle = React.useMemo(
    () => ({
      backgroundColor: c.companyColors[solarCompany],
      '&:hover': { backgroundColor: c.companyColors[solarCompany] },
      width: 256,
    }),
    [solarCompany],
  )

  const companyState = c.getCompanyState(solarCompany)
  const companyLongName =
    contractCompanyFull.longNames?.[companyState] ??
    contractCompanyFull.longName

  return (
    <ReportPages companyBorder customerID={customerID} hPad noPDF={noPDF}>
      <div style={sx.logo}>
        <ContractLogo isPDF={isPDF} readOnly={readOnly} size="l" />
      </div>
      {gapBig}
      <div style={sx.headerText}>
        <p className={styles['heading']} style={sx.headerHeading}>
          {companyLongName}
        </p>

        <p style={sx.companyInfo}>
          {contractCompanyFull.address1s?.[companyState] ??
            contractCompanyFull.address1}
        </p>
        <p style={sx.companyInfo}>
          {contractCompanyFull.address2s?.[companyState] ??
            contractCompanyFull.address2}
        </p>
        <p style={sx.companyInfo}>{contractCompanyFull.email}</p>
        <p style={sx.companyInfo}>
          License #:
          {contractCompanyFull.roofingLicenses?.[companyState] ??
            contractCompanyFull.roofingLicense}
        </p>
        <p style={sx.companyInfo}>
          {contractCompanyFull.phoneNumbers?.[companyState] ??
            contractCompanyFull.phoneNumber}
        </p>
        {contractCompanyFull.website && (
          <p style={sx.companyInfo}>{contractCompanyFull.website}</p>
        )}
      </div>
      {gapMedium}
      <p className={styles['subheading']}>CERTIFICATE OF COMPLETION</p>
      {gapMedium}
      <p style={sx.node}>
        Buyer:{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerName}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        Co-Buyer:{' '}
        <input
          className={styles['input-inline-wide']}
          disabled={
            readOnly ||
            !!security_completion_sign_customer ||
            !!security_contract_sign_company ||
            !!security_contract_sign_customer
          }
          onChange={coBuyerHandle}
          title="Co-Buyer"
          value={coBuyer}
        />
      </p>
      <p style={sx.node}>
        Property Address:{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerAddress}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </p>
      <p style={sx.node}>
        Home / Cell Home:
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerPhone}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        E-mail:
        <span className={styles['underlined']}>
          {blankChar(8)}
          {customerEmail}
          {blankChar(8)}
        </span>
      </p>
      <p style={sx.node}>
        Work Order / Project #:
        <input
          className={styles['input-inline-wide']}
          disabled={readOnly || !!security_completion_sign_customer}
          onChange={workOrderHandle}
          title="Work order number"
          value={workOrderVal}
        />
      </p>
      <p style={sx.node}>
        Completion Date:
        <input
          className={styles['input-inline-wide']}
          disabled={readOnly || !!security_completion_sign_customer}
          onChange={completionDateHandle}
          title="Completion date"
          value={completionDateVal}
        />
      </p>
      {gapSmall}
      <p style={sx.node}>
        Rep Name:
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {c.displayHomeRepOnContract(homeRep)}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        Rep Phone:
        <span className={styles['underlined']}>
          {blankChar(8)}
          n/a
          {blankChar(8)}
        </span>
      </p>
      {gapMedium}
      <p style={sx.node}>
        <span style={sx.bold}>1. Scope of Completed Work.</span> This document
        certifies that {companyLongName} has completed the installation and
        commissioning of the following security system at the service address
        listed above:
      </p>
      <div style={gs.flowRootWidth100}>
        <p style={sx.node}>System Installed:</p>
        {detailLines.map(({ id, name, qty }) => (
          <p key={id} style={sx.nodeBullet}>
            {qty === -1 ? `• ${name}` : `• ${qty} x ${name}`}
          </p>
        ))}
      </div>
      <div style={gs.flowRootWidth100}>
        <p style={sx.node}>Services Included:</p>

        <p style={sx.nodeBullet}>• Professional installation & labor</p>
        <p style={sx.nodeBullet}>• System configuration</p>
        <p style={sx.nodeBullet}>• Mobile application & remote access setup</p>
        <p style={sx.nodeBullet}>• Basic customer orientation / handoff</p>
      </div>
      {gapMedium}
      <div style={gs.flowRootWidth100}>
        <p style={sx.node}>
          <span style={sx.bold}>2. Installation Verification.</span> The
          contractor confirms that, at the time of completion, the following
          verifications were performed:
        </p>

        <p style={sx.nodeBullet}>• Equipment securely mounted and powered</p>
        <p style={sx.nodeBullet}>• Live camera views confirmed</p>
        <p style={sx.nodeBullet}>• Recording functionality verified</p>
        <p style={sx.nodeBullet}>• App access and remote viewing enabled</p>
        <p style={sx.nodeBullet}>• System walkthrough provided to client</p>
        <p style={sx.nodeBullet}>• Work area left safe and orderly</p>
      </div>
      {gapMedium}
      <div style={gs.flowRootWidth100}>
        <p style={sx.node}>
          <span style={sx.bold}>3. Client Acknowledgment.</span> By signing
          below, the client acknowledges that:
        </p>

        <p style={sx.nodeBullet}>
          • The system described above has been installed and demonstrated
        </p>
        <p style={sx.nodeBullet}>
          • The system was operational at the time of completion
        </p>
        <p style={sx.nodeBullet}>
          • Any future changes, upgrades, or troubleshooting beyond this scope
          may require additional service fees
        </p>
        <p style={sx.nodeBullet}>
          • Ongoing app access, cloud features, or manufacturer services are
          subject to third-party terms and availability
        </p>
      </div>
      {gapMedium}
      {/* This flow-root ensures signatures aren't awkwardly by themselves on
        a lone page, tying them together with the last paragraph of 
        conditions */}
      <div style={gs.flowRootWidth100}>
        <p style={sx.node}>
          <span style={sx.bold}>4. Warranty & Limitations.</span>
        </p>

        <p style={sx.nodeBullet}>
          • Equipment warranties are provided by the manufacturer
        </p>
        <p style={sx.nodeBullet}>
          • Contractor is not responsible for internet outages, manufacturer app
          changes, or third-party service disruptions
        </p>

        {gapBig}

        <div className={styles['signatures']}>
          <div className={styles['signature-container']}>
            <div>
              <p className={styles['label-signature']}>Property owner:</p>

              <span>{customerName}</span>
            </div>

            {gs.rowGap24}

            {security_completion_sign_customer && (
              <>
                <img
                  alt=""
                  className={styles['signature-img']}
                  src={security_completion_sign_customer}
                  width={200}
                />
                <p>
                  {c.formatContractDate(
                    security_completion_sign_customer_date,
                    solarCompany,
                    'MM/DD/YYYY - hh:mma',
                  )}
                </p>
              </>
            )}

            <p>Address: {customerAddress}</p>

            {gs.rowGap24}

            {isPDF || security_completion_sign_customer ? (
              <div />
            ) : (
              <Button
                onClick={toggleSecCompletionSignature}
                sx={buttonSignatureStyle}
                variant="contained"
              >
                Client Signature
              </Button>
            )}
          </div>

          <div className={styles['signature-container']}>
            <div>
              <p className={styles['label-signature']}></p>
              <span> </span>
            </div>

            {gs.rowGap24}

            <p className={styles['label-signature']}></p>
            <p className={styles['label-signature']}></p>

            {gs.rowGap24}

            <div />
          </div>
        </div>
      </div>
    </ReportPages>
  )
})

const gapBig = gs.rowGap48
const gapMedium = gs.rowGap32
const gapSmall = gs.rowGap16

const sxNode = {
  alignSelf: 'flex-start',
  fontFamily: c.light.fontFamily,
  margin: 0,
}
const sx = {
  companyInfo: {
    fontFamily: c.light.fontFamily,
    margin: 0,
    textAlign: 'left' as const,
  },
  headerHeading: { textAlign: 'left' as const },
  // clears the absolutely-positioned logo (200px wide) to its left - the
  // company longName can be much longer than the original "First Electric"
  // (e.g. "WNT, LLC dba First Electric"), and centered text of that length
  // used to render underneath/behind the logo.
  headerText: { marginLeft: 220 },
  logo: { left: horizontalPad, position: 'absolute' as const, top: '96px' },
  node: sxNode,
  nodeBullet: { ...sxNode, marginLeft: '24px' },
  bold: { ...sxNode, fontWeight: 'bold' },
  boldCursive: { ...sxNode, fontStyle: 'italic', fontWeight: 'bold' },
}

export const getCustomFieldsQties = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.numbers =>
  activeCustomFields.map(
    (field): number =>
      customerData[(field + '_qty') as c.CustomerField] as number,
  )

export const getCustomFieldsNames = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.strings =>
  activeCustomFields.map(
    (field): string =>
      customerData[(field + '_name') as c.CustomerField] as string,
  )

export const getCustomFieldsCosts = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.strings =>
  activeCustomFields.map(
    (field): string =>
      customerData[(field + '_customer_cost') as c.CustomerField] as string,
  )

export const getCustomFieldsNotes = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
) =>
  activeCustomFields.map((field): c.strings => {
    const notes = c.customerNotes(customerData, field)

    return notes.split('\n').filter((_) => !!_)
  })

const blankChar = (qty: number): string =>
  new Array(qty).fill(String.fromCharCode(160)).join('')
